import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Radar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/client/reset-password", { token, password });
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md card-surface p-8 lg:p-10" data-testid="reset-password-page">
        <div className="flex items-center gap-2.5">
          <span className="flex items-center justify-center w-10 h-10 rounded-md bg-blue-600/20 border border-blue-500/30">
            <Radar className="w-5 h-5 text-cyan-400" />
          </span>
          <span className="font-display font-bold text-xl text-white">GEOTRAQ <span className="text-cyan-400">AI</span></span>
        </div>
        <h1 className="font-display text-2xl font-bold text-white mt-6">Set a new password</h1>
        {!token ? (
          <p className="mt-4 text-sm text-red-400">Invalid reset link. Please request a new one from the login page.</p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
            <div>
              <Label className="text-slate-300">New Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="mt-1.5 bg-secondary/50 border-white/10 h-11" data-testid="reset-new-password" />
            </div>
            <div>
              <Label className="text-slate-300">Confirm Password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6}
                className="mt-1.5 bg-secondary/50 border-white/10 h-11" data-testid="reset-confirm-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 font-semibold h-11" data-testid="reset-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
            </Button>
          </form>
        )}
        <Link to="/login" className="block mt-6 text-sm text-cyan-400 hover:text-cyan-300">Back to login</Link>
      </div>
    </div>
  );
}
